import { Link } from "react-router-dom";
import { GradeBadge, Timestamp } from "@/components/common";

export function CandidateCard({ c, runId }) {
  const isVessel = c.source_type === "VESSEL";
  return (
    <article data-testid={`candidate-card-${runId}-${c.rank}`} className="rounded border border-[#1E3E30] border-l-4 border-l-amber-500 bg-[#11241C] p-4 transition-transform duration-200 hover:-translate-y-px">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl font-bold text-amber-300">#{c.rank}</span>
            {isVessel ? (
              <Link to={`/vessels/${c.source_key}`} data-testid={`candidate-link-${c.source_key}`} className="font-semibold text-slate-100 underline-offset-2 hover:underline">{c.label}</Link>
            ) : (
              <span className="font-semibold text-slate-100">{c.label}</span>
            )}
          </div>
          <div className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-slate-500">
            {c.source_type.replace("_", " ")}{c.mmsi ? ` · MMSI ${c.mmsi}` : ""} · {c.distance_m} m @ {c.bearing_deg}°
          </div>
        </div>
        <GradeBadge grade={c.evidence_grade} testId={`candidate-grade-${runId}-${c.rank}`} />
      </header>
      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span>Relative ranking score <span className="text-slate-600">(uncalibrated, not a probability)</span></span>
          <span className="font-mono text-slate-300" data-testid={`candidate-score-${runId}-${c.rank}`}>{c.ranking_score.toFixed(3)}</span>
        </div>
        <div className="mt-1 h-1.5 w-full rounded bg-slate-800"><div className="h-1.5 rounded bg-amber-500/70" style={{ width: `${Math.min(100, c.ranking_score * 100)}%` }} /></div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-emerald-400">For</div>
          <ul className="space-y-1 text-slate-300">{c.for_evidence.map((r, i) => <li key={i} className="flex gap-1.5"><span className="text-emerald-500">+</span>{r}</li>)}</ul>
        </div>
        <div>
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-rose-300">Against</div>
          <ul className="space-y-1 text-slate-300">{c.against_evidence.map((r, i) => <li key={i} className="flex gap-1.5"><span className="text-rose-400">−</span>{r}</li>)}</ul>
        </div>
      </div>
      <details className="mt-3 text-xs">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-300">Feature breakdown</summary>
        <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1 font-mono text-[11px] text-slate-400">
          {Object.entries(c.features).map(([k, v]) => <div key={k} className="flex justify-between"><span>{k}</span><span className="text-slate-200">{v.toFixed(2)}</span></div>)}
        </div>
        {c.t_release && <div className="mt-2 text-slate-500">Release time: <Timestamp ts={c.t_release} /> · AIS at release: <span className="font-mono text-slate-300">{c.ais_status_at_release || "n/a"}</span>{c.berth_corroboration && <> · berth record {c.berth_corroboration.berth_code}</>}</div>}
      </details>
      <footer className="mt-3 border-t border-[#1E3E30] pt-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Provisional automated score — not a regulatory finding</footer>
    </article>
  );
}
